import { Checkbox, DateTimeInput, Field, NumericInput, RadioGroup, TextInput, TextOutput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { FieldSet, ListFieldSection } from 'Components/Layout';
import { WorksheetAssociatedTestSection } from 'Components/Worksheets/Core';
import { getMessagesForPath } from 'Components/Worksheets/Core/Helpers/messageHelpers';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { valueOrDefault } from 'Lib/Any';
import { DateTimeDataFormat } from 'Lib/Format';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import externalTestMethods from '../../../ExternalTestMethods';

let AsCuringSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const importedLiquidLimitTestId = useSelector(
    createWorksheetSelector!('Curing.AssociatedLiquidLimit.ImportedTest.Id')
  );
  const sandOrGranularMaterialChecked = useSelector(createWorksheetSelector!('Curing.SandOrGranularMaterial'));
  const LiquidLimitDetermination = useSelector(createWorksheetSelector!('Curing.LiquidLimitDetermination'));
  const stabiliserUsed = useSelector(
    pipe(createWorksheetSelector!('Preparation.StabiliserUsed'), valueOrDefault(false))
  ) as boolean;
  const requiredCuringTimeMessages = useSelector(
    pipe(createWorksheetSelector!('Curing'), (c) => getMessagesForPath(c, 'RequiredCuringTime'))
  );
  const useSameCuringForAllTestPortions =
    useSelector(createWorksheetSelector!('Curing.CuringAppliesToSubSamples')) ?? false;

  return (
    <>
      <ListFieldSection>
        <FieldSet>
          <Field input={<Checkbox />} name="Curing.SandOrGranularMaterial" />
          <Field input={<Checkbox />} name="Curing.ForCompactionControl" />
        </FieldSet>
        <Field
          visible={!sandOrGranularMaterialChecked}
          input={<RadioGroup />}
          name={`Curing.LiquidLimitDetermination`}
          options={['Measured', 'Source Records', 'Visual/Tactile'].map((e) => ({ label: e, value: e }))}
          confirm
          confirmText="Changing Liquid Limit Determination will clear all related Liquid Limit data"
        />
      </ListFieldSection>
      {!sandOrGranularMaterialChecked && LiquidLimitDetermination === 'Measured' && (
        <WorksheetAssociatedTestSection
          name="Curing.AssociatedLiquidLimit"
          testType="Liquid Limit"
          associatedTestModel={{
            autoImported: true,
            imported: {
              includeDescendants: true,
              importedTestResults: (
                <ListFieldSection>
                  <Field input={<TextOutput />} name="Curing.LiquidLimit" emph />
                </ListFieldSection>
              ),
              importModel: {
                Id: importedLiquidLimitTestId,
              },
            },
            external: {
              testMethods: [
                externalTestMethods.liquidLimit.AS_1289_3_1_1,
                externalTestMethods.liquidLimit.AS_1289_3_1_2,
                externalTestMethods.liquidLimit.AS_1289_3_9_1,
                externalTestMethods.liquidLimit.AS_1289_3_9_2,
              ],
              permitOtherTestMethods: false,
              externalTestResults: (
                <ListFieldSection>
                  <Field
                    input={<NumericInput />}
                    name="Curing.AssociatedLiquidLimit.ExternalTest.Results.LiquidLimit"
                  />
                </ListFieldSection>
              ),
            },
          }}
        />
      )}

      <ListFieldSection visible={!sandOrGranularMaterialChecked && LiquidLimitDetermination === 'Source Records'}>
        <Field input={<TextInput />} name="Curing.LiquidLimitSourceRecord" />
        <Field input={<NumericInput />} name="Curing.LiquidLimit" />
      </ListFieldSection>

      <ListFieldSection visible={!sandOrGranularMaterialChecked && LiquidLimitDetermination === 'Visual/Tactile'}>
        <Field
          input={<RadioGroup />}
          name={`Curing.LiquidLimitEstimatedCategory`}
          options={['Low', 'Medium', 'High'].map((e) => ({ label: e, value: e }))}
        />
      </ListFieldSection>
      <ListFieldSection visible={requiredCuringTimeMessages.length !== 0} messages={requiredCuringTimeMessages} />
      <ListFieldSection wide>
        <Field
          input={<Checkbox />}
          name="Curing.CuringAppliesToSubSamples"
          confirm={({ value }) => value === true}
          confirmText="The curing conditions will be used for all test portions, overwriting any existing data."
        />
      </ListFieldSection>
      <ListFieldSection visible={useSameCuringForAllTestPortions}>
        <Field
          input={<DateTimeInput />}
          name="Curing.PortionCuring.StartedAtDateTime"
          dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}
        />
        <Field
          input={<DateTimeInput />}
          name="Curing.PortionCuring.CompletedAtDateTime"
          dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}
        />
        <Field input={<TextOutput />} name="Curing.PortionCuring.Duration" />
        <Field
          visible={stabiliserUsed}
          input={<TextOutput />}
          name="Curing.PortionCuring.TimeBetweenAdditionOfStabiliserAndCompaction"
        />
      </ListFieldSection>
    </>
  );
};

AsCuringSection = withInjectedProps(AsCuringSection);

export { AsCuringSection };
export default AsCuringSection;
